import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import client from '../api/client';
import { toastError, toastSuccess } from '../toastHelper';
import SourcesTable from './SourcesTable';
import TrackableLinkModal from './TrackableLinkModal';
import './marketingShared.css';
import './TrafficAttribution.css';

const SourcesLinksCard = ({
  teamId,
  confirmId,
  csrfToken,
  trackableRows: trackableRowsProp,
  leftoverRows: leftoverRowsProp,
  referringSiteRows: referringSiteRowsProp,
  paidRows: paidRowsProp,
  trackableLinks: trackableLinksProp,
  ticketTypeOptions: ticketTypeOptionsProp,
  websiteUrl: websiteUrlProp,
  trackingSince: trackingSinceProp,
  sectionNumber,
}) => {
  const hasInjectedData =
    referringSiteRowsProp !== undefined ||
    trackableRowsProp !== undefined ||
    trackableLinksProp !== undefined;
  const SERVER_RANGE = 'since_tracking';
  const trackingSince = trackingSinceProp ?? null;

  const [range, setRange] = useState(SERVER_RANGE);
  const [fetchedData, setFetchedData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch when there is no injected data (e.g. EventPage), or when the user
  // picks a range other than what the server rendered (since_tracking).
  const needsFetch = (!hasInjectedData && Boolean(teamId) && Boolean(confirmId)) || range !== SERVER_RANGE;
  const [loading, setLoading] = useState(needsFetch);

  useEffect(() => {
    if (!needsFetch) {
      setFetchedData(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    client
      .get(`/teams/${teamId}/confirms/${confirmId}/marketing_insights/sources`, {
        params: { range },
        signal: controller.signal,
      })
      .then((response) => {
        setFetchedData(response.data);
        setLinks(response.data?.trackable_links || []);
      })
      .catch((err) => {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        setError(err);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [needsFetch, range, teamId, confirmId]);

  const useFetched = Boolean(fetchedData) && (range !== SERVER_RANGE || !hasInjectedData);
  const pickRows = (fetchedKey, injectedProp) =>
    useFetched ? (fetchedData?.[fetchedKey] || []) : (hasInjectedData ? (injectedProp || []) : []);

  const trackableRows = pickRows('trackable_rows', trackableRowsProp);
  const leftoverRows = pickRows('leftover_rows', leftoverRowsProp);
  const referringSiteRows = pickRows('referring_site_rows', referringSiteRowsProp);
  const paidRows = pickRows('paid_rows', paidRowsProp);
  const ticketTypeOptions = useFetched
    ? (fetchedData?.ticket_type_options || [])
    : (ticketTypeOptionsProp || []);
  const websiteUrl = useFetched ? (fetchedData?.website_url || null) : (websiteUrlProp || null);
  const effectiveTrackingSince = trackingSince ?? (fetchedData?.tracking_since ?? null);

  // Keep a local working copy of links so CRUD updates are reflected immediately
  // without requiring a full page reload.
  const [links, setLinks] = useState(trackableLinksProp || []);
  useEffect(() => { setLinks(trackableLinksProp || []); }, [trackableLinksProp]);

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [editingLink, setEditingLink] = useState(null);

  const openCreateModal = () => { setEditingLink(null); setModalMode('create'); };
  const closeModal = () => setModalMode(null);

  const findLinkById = (id) => links.find((l) => l.id === id);

  const handleEdit = (trackableLinkId) => {
    const link = findLinkById(trackableLinkId);
    if (!link) return;
    setEditingLink(link);
    setModalMode('edit');
  };

  const handleDelete = async (trackableLinkId) => {
    if (!window.confirm('Delete this trackable link?')) return;

    let response;
    try {
      response = await fetch(`/teams/${teamId}/confirms/${confirmId}/trackable_links/${trackableLinkId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken }
      });
    } catch (err) {
      toastError(err.message || 'Unable to delete link');
      return;
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      toastError((body.errors || ['Unable to delete link']).join(', '));
      return;
    }

    setLinks((prev) => prev.filter((l) => l.id !== trackableLinkId));
    toastSuccess('Trackable link deleted.');
  };

  const handleSaved = (savedLink) => {
    if (!savedLink || savedLink.id == null) return;
    setLinks((prev) => {
      const idx = prev.findIndex((l) => l.id === savedLink.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = savedLink;
        return next;
      }
      return [savedLink, ...prev];
    });
    toastSuccess('Trackable link saved.');
    closeModal();
  };

  return (
    <>
      <div className="mi-section-heading">
        <h2>
          {sectionNumber !== undefined && <span className="mi-section-num">{sectionNumber}</span>}
          <span className="mi-section-title">Sources &amp; trackable links</span>
        </h2>
      </div>

      <div className="mi-tracking-controls">
        <span className="mi-tracking-since">
          {effectiveTrackingSince
            ? `Site tracking active since ${new Date(effectiveTrackingSince).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
            : 'Site tracking not yet active'}
        </span>
        <div className="mi-range-toggle" role="group" aria-label="Data range">
          <button
            type="button"
            className={range === 'since_tracking' ? 'mi-range-btn is-active' : 'mi-range-btn'}
            aria-pressed={range === 'since_tracking'}
            disabled={!effectiveTrackingSince}
            title={effectiveTrackingSince ? undefined : 'No tracked visits yet'}
            onClick={() => setRange('since_tracking')}
          >
            Since tracking
          </button>
          <button
            type="button"
            className={range === 'all' ? 'mi-range-btn is-active' : 'mi-range-btn'}
            aria-pressed={range === 'all'}
            onClick={() => setRange('all')}
          >
            All time
          </button>
        </div>
      </div>

      {loading && (
        <div className="mi-sources-loading" role="status" aria-live="polite">
          Loading sources…
        </div>
      )}
      {error && !loading && (
        <div className="mi-sources-error" role="alert">
          Couldn't load sources. Refresh to try again.
        </div>
      )}
      {!loading && !error && (
        <>
          <SourcesTable
            id="referring-sites"
            title="Top referring sites"
            subtitle="Where checkout visitors came from, matched by referring site or UTM source. “Visits” counts checkout-page loads, not event-page views. Excludes paid ads and trackable links."
            rows={referringSiteRows}
          />
          <SourcesTable
            title="Trackable links"
            subtitle="Your trackable links, plus any other UTM-tagged traffic that isn't a known referring site or paid ad."
            emptyText="No traffic recorded in this window."
            rows={[...trackableRows, ...leftoverRows]}
            links={links}
            teamId={teamId}
            confirmId={confirmId}
            onCreateTrackableLink={openCreateModal}
            onEditTrackableLink={handleEdit}
            onDeleteTrackableLink={handleDelete}
          />
          <SourcesTable
            id="paid-sources"
            title="Paid"
            subtitle="Paid ad campaigns, identified by a paid medium or a linked Meta/Google ad. Excludes organic and trackable-link traffic."
            emptyText="No paid traffic in this window."
            rows={paidRows}
          />
        </>
      )}

      {modalMode && (
        <TrackableLinkModal
          confirmId={confirmId}
          teamId={teamId}
          csrfToken={csrfToken}
          link={editingLink}
          ticketTypeOptions={ticketTypeOptions}
          websiteUrl={websiteUrl}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

SourcesLinksCard.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  confirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  csrfToken: PropTypes.string,
  trackableRows: PropTypes.array,
  leftoverRows: PropTypes.array,
  referringSiteRows: PropTypes.array,
  paidRows: PropTypes.array,
  trackableLinks: PropTypes.array,
  ticketTypeOptions: PropTypes.array,
  websiteUrl: PropTypes.string,
  trackingSince: PropTypes.string,
  sectionNumber: PropTypes.number,
};

export default SourcesLinksCard;
